import { useCallback, useEffect, useState } from "react";
import { fetchNotifications, markNotificationsRead } from "../api";
import { connectSocket, getSocket } from "../socket";
import { parseNotifications } from "../utils/parsers";

// The bell: loads the newest notifications over REST once, then follows the
// Socket-Server's "bellNotification" broadcasts (the same rows, sent after
// every finished job, stop or re-queue). markAllRead() clears the badge
// locally first and tells the backend afterwards.
export default function useNotifications(enabled) {
  const [items, setItems] = useState([]);
  const [unread, setUnread] = useState(0);
  const [status, setStatus] = useState("idle");
  const [error, setError] = useState(null);

  const apply = useCallback((list, unreadTotal) => {
    setItems(list);
    setUnread(
      typeof unreadTotal === "number"
        ? unreadTotal
        : list.filter((item) => !item.isRead).length,
    );
  }, []);

  useEffect(() => {
    if (!enabled) {
      setItems([]);
      setUnread(0);
      setStatus("idle");
      return undefined;
    }
    let cancelled = false;
    setStatus("loading");
    setError(null);

    fetchNotifications()
      .then((data) => {
        if (cancelled) return;
        apply(parseNotifications(data) ?? [], data?.unread);
        setStatus("ready");
      })
      .catch((err) => {
        if (cancelled) return;
        setStatus("error");
        setError(err?.message || "Request failed");
      });

    connectSocket();
    const socket = getSocket();
    const handleBell = (raw) => {
      const list = parseNotifications(raw);
      if (!list) return;
      apply(list);
      setStatus("ready");
      setError(null);
    };
    socket.on("bellNotification", handleBell);

    return () => {
      cancelled = true;
      socket.off("bellNotification", handleBell);
    };
  }, [enabled, apply]);

  const markAllRead = useCallback(async () => {
    if (unread === 0) return;
    setItems((prev) => prev.map((item) => (item.isRead ? item : { ...item, isRead: true })));
    setUnread(0);
    try {
      await markNotificationsRead({ all: true });
    } catch {
      // The next load shows them unread again; nothing else to do here.
    }
  }, [unread]);

  return { items, unread, status, error, markAllRead };
}
